// Hörertelefon: die Warteschlange aus der Suite heraus führen.
//
// Hörer melden sich über die Hörerseite und landen hier in der Schlange. Die Suite
// ordnet nur: vorhören, auf Sendung nehmen, auflegen, im Notfall sperren. Der Ton
// selbst läuft im Studio – dort hängt das Mikrofon und dort kommt der Anrufer an.
//
// Sichtbar ist die Seite nur, wenn das Modul „Hörertelefon“ eingeschaltet ist
// (Aussehen → Mitmach-Module).

import { useCallback, useEffect, useRef, useState } from "react";

import { api, phoneApi, type PhoneBlocked, type PhoneCall, type PhoneQueueEntry, type PhoneState } from "../api";
import { useAuth } from "../auth/AuthProvider";
import { errorText } from "../components/streaming-ui";
import { Initialen, KanalWahl, Meldungen, ModulAus, Sym } from "../components/social-ui";
import { Badge, Card, EmptyState, LiveDot, Page, PageHeader } from "../components/ui";
import type { Channel } from "../types";

/** Uhrzeit ohne Sekunden – mehr braucht in der Sendung niemand. */
const clock = (iso: string): string => new Date(iso).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });

const dayAndClock = (iso: string): string =>
  new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });

/** Sendezeit als Minuten:Sekunden, damit man sie ohne Rechnen liest. */
function airTime(seconds: number): string {
  if (!seconds) return "–";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

/** Warum der Anruf vorbei ist – in Worten statt in Kürzeln. */
const endWords: Record<string, string> = {
  listener: "aufgelegt",
  studio: "Studio",
  blocked: "gesperrt",
  timeout: "abgelaufen",
};

/** Farbiges Schildchen für den Stand eines Anrufs. */
function StatusBadge({ status }: { status: PhoneQueueEntry["status"] }): JSX.Element {
  const looks: Record<string, { text: string; tone: "neutral" | "warning" | "danger" }> = {
    waiting: { text: "wartet", tone: "neutral" },
    preview: { text: "wird vorgehört", tone: "warning" },
    air: { text: "auf Sendung", tone: "danger" },
    done: { text: "vorbei", tone: "neutral" },
  };
  const look = looks[status] ?? looks.done!;
  return (
    <Badge tone={look.tone}>
      {status === "air" && <LiveDot className="h-1.5 w-1.5" />}
      {status === "preview" && <Sym name="headphones" className="h-3 w-3" />}
      {look.text}
    </Badge>
  );
}

export function PhonePage(): JSX.Element {
  const { user } = useAuth();
  const mayRun = user?.role === "moderator" || user?.role === "tenant_admin" || user?.role === "super_admin";

  const [channels, setChannels] = useState<Channel[]>([]);
  const [slug, setSlug] = useState<string>("");
  const [phoneOn, setPhoneOn] = useState<boolean | null>(null);
  const [state, setState] = useState<PhoneState | null>(null);
  const [calls, setCalls] = useState<PhoneCall[]>([]);
  const [blocked, setBlocked] = useState<PhoneBlocked[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const timer = useRef<number | null>(null);

  useEffect(() => {
    api.listChannels().then((list) => {
      setChannels(list);
      setSlug((s) => s || list[0]?.slug || "");
    }).catch((err) => setError(errorText(err)));
    api.listModules()
      .then((list) => setPhoneOn(list.find((m) => m.id === "phone")?.enabled === true))
      .catch((err) => { setPhoneOn(false); setError(errorText(err)); });
  }, []);

  const load = useCallback(async (): Promise<void> => {
    if (!slug || phoneOn !== true) return;
    try {
      const [now, list, blocks] = await Promise.all([phoneApi.state(slug), phoneApi.calls(slug), phoneApi.blocked(slug)]);
      setState(now);
      setCalls(list);
      setBlocked(blocks);
    } catch (err) {
      setError(errorText(err));
    }
  }, [slug, phoneOn]);

  // Solange die Leitung offen ist, alle fünf Sekunden nachsehen – dann wächst die
  // Wartezeit in der Liste mit. Ist zu, reicht der Blick alle zwanzig Sekunden.
  const open = state?.open === true;
  useEffect(() => {
    void load();
    if (timer.current) window.clearInterval(timer.current);
    timer.current = window.setInterval(() => void load(), open ? 5000 : 20000);
    return () => { if (timer.current) window.clearInterval(timer.current); };
  }, [load, open]);

  const act = async (what: () => Promise<unknown>, message: string): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await what();
      setNotice(message);
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  if (phoneOn === false) {
    return (
      <ModulAus title="Hörertelefon" icon="phone">
        „Hörertelefon“ ist nicht eingeschaltet. Das geht unter Aussehen → Mitmach-Module.
      </ModulAus>
    );
  }

  const queue = state?.queue ?? [];
  const aufSendung = queue.find((c) => c.status === "air");

  return (
    <Page className="max-w-6xl">
      <PageHeader
        eyebrow="Hörer"
        title="Hörertelefon"
        description={<>
          Hier steht, wer in der Leitung wartet, und hier wird sortiert. Gehört und gesendet wird im Studio –
          die Suite schiebt nur die Anrufe hin und her.
          {!mayRun && " Anrufe führen dürfen nur Moderation und Admins; ihr seht die Listen zum Mitlesen."}
        </>}
        actions={<KanalWahl channels={channels} slug={slug} onChange={setSlug} />}
      />

      <Meldungen error={error} notice={notice} onClear={() => setNotice(null)} />

      {/* ---------- Die Leitung ---------- */}
      <section className={`card mb-5 overflow-hidden ${state?.open ? "brand-glow" : ""}`}>
        <div className="flex flex-wrap items-center gap-5 p-5">
          <div className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl ${state?.open ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400" : "bg-input text-muted"}`}
            style={state?.open ? undefined : { boxShadow: "inset 0 0 0 1px var(--border)" }}>
            <Sym name="phone" className="h-6 w-6" />
          </div>
          <div className="min-w-0 flex-1 basis-60">
            <div className="section-label">Die Leitung</div>
            {state?.open ? (
              <div className="mt-0.5 text-lg font-semibold text-primary">
                Telefon an{state.open_until ? ` – bis ${clock(state.open_until)} Uhr` : ""}
                {state.opened_by && <span className="ml-1.5 text-sm font-normal text-muted">({state.opened_by})</span>}
              </div>
            ) : (
              <>
                <div className="mt-0.5 text-lg font-semibold text-primary">Das Telefon ist zu.</div>
                <div className="text-sm text-muted">Hörer können gerade nicht anrufen.</div>
              </>
            )}
            <div className="mt-1.5 flex flex-wrap items-center gap-2">
              <Badge tone={state?.studio_connected ? "success" : "neutral"} dot>
                {state?.studio_connected ? "Studio ist verbunden" : "Studio ist nicht verbunden"}
              </Badge>
              {aufSendung && <Badge tone="danger"><LiveDot className="h-1.5 w-1.5" />{aufSendung.name} ist auf Sendung</Badge>}
            </div>
          </div>
          <div className="flex items-center gap-5">
            <div className="text-right">
              <div className="text-2xl font-semibold tabular-nums leading-none text-primary">
                {queue.length}{state && <span className="text-base font-normal text-muted"> / {state.queue_max}</span>}
              </div>
              <div className="mt-1 text-xs text-muted">in der Leitung</div>
            </div>
            {state?.open && mayRun && (
              <button
                disabled={busy}
                onClick={() => window.confirm("Telefon zumachen? Alle Anrufer in der Leitung werden dabei aufgelegt.")
                  && void act(() => phoneApi.setOpen(slug, false), "Telefon ist zu. Die Leitung ist frei.")}
                className="btn btn-secondary"
              >
                Telefon zumachen
              </button>
            )}
          </div>
        </div>
        <p className="border-t border-default bg-surface-sunken/60 px-5 py-2.5 text-xs text-muted">
          Aufmachen geht nur im Studio: Dort wird der Schlüssel für den Ton vergeben, den die Suite nicht hat.
          {state ? ` Höchstens ${state.queue_max} Hörer dürfen gleichzeitig warten.` : ""}
        </p>
      </section>

      <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_20rem]">
        {/* ---------- Warteschlange ---------- */}
        <Card title="Warteschlange" padded={false} className="min-w-0" actions={queue.length > 0 ? <Badge tone="brand">{queue.length}</Badge> : undefined}>
          {queue.length === 0 ? (
            <div className="p-5">
              <EmptyState icon={<Sym name="phone" className="h-5 w-5" />} title="Niemand in der Leitung." />
            </div>
          ) : (
            <ul className="divide-y divide-default">
              {queue.map((c) => (
                <li key={c.id} className={`flex flex-wrap items-center gap-3 px-5 py-3.5 text-sm ${c.status === "air" ? "bg-red-500/[0.05]" : c.status === "preview" ? "bg-amber-500/[0.05]" : ""}`}>
                  <span className="w-5 shrink-0 text-right text-sm font-semibold tabular-nums text-muted">{c.status === "waiting" ? c.position : "–"}</span>
                  <span className="relative">
                    <Initialen name={c.name} />
                    <span
                      className={`absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full ring-2 ring-[var(--bg-card)] ${c.connected ? "bg-emerald-500" : "bg-line-strong"}`}
                      title={c.connected ? "Der Hörer ist in der Leitung" : "Der Hörer ist gerade nicht da"}
                    />
                  </span>
                  <span className="min-w-0 flex-1 basis-40">
                    <span className="block font-medium text-primary">{c.name}{c.place ? ` aus ${c.place}` : ""}</span>
                    <span className="flex flex-wrap items-center gap-x-2 text-xs text-muted">
                      {c.topic && <span className="text-secondary">„{c.topic}“</span>}
                      <span className="inline-flex items-center gap-1"><Sym name="clock" className="h-3 w-3" />wartet {c.waiting_text}</span>
                    </span>
                  </span>
                  <StatusBadge status={c.status} />
                  {mayRun && (
                    <span className="flex w-full flex-wrap items-center gap-1.5 sm:w-auto">
                      {c.status !== "preview" && (
                        <button disabled={busy} onClick={() => void act(() => phoneApi.preview(c.id), "Liegt jetzt auf dem Kopfhörer.")}
                          className="btn btn-secondary btn-sm"
                          title="Nur im Kopfhörer hören, noch nicht auf Sendung"><Sym name="headphones" className="h-3.5 w-3.5" />Vorhören</button>
                      )}
                      {c.status !== "air" && (
                        <button disabled={busy} onClick={() => void act(() => phoneApi.air(c.id), "Der Anruf ist auf Sendung.")}
                          className="btn btn-primary btn-sm"><Sym name="radio" className="h-3.5 w-3.5" />Auf Sendung</button>
                      )}
                      <button disabled={busy} onClick={() => void act(() => phoneApi.hangup(c.id), "Aufgelegt.")}
                        className="btn btn-secondary btn-sm">Auflegen</button>
                      <button disabled={busy}
                        onClick={() => window.confirm("Anrufe und Nachrichten von diesem Gerät bis heute Nacht nicht mehr annehmen?")
                          && void act(() => phoneApi.block(c.id), "Gesperrt. Von diesem Gerät kommt heute nichts mehr.")}
                        className="btn btn-ghost btn-sm btn-icon !text-red-600 dark:!text-red-400"
                        title="Auflegen und das Gerät für heute sperren" aria-label="Sperren"><Sym name="ban" className="h-3.5 w-3.5" /></button>
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
          <p className="border-t border-default px-5 py-3 text-xs leading-relaxed text-muted">
            Der Punkt am Bild ist grün, solange der Hörer wirklich in der Leitung sitzt. „Vorhören“ legt ihn auf den
            Kopfhörer, „Auf Sendung“ schaltet ihn in den Stream. Beides gibt es nur einmal: Wer dort schon liegt,
            geht zurück in die Schlange.
          </p>
        </Card>

        {/* ---------- Sperrliste ---------- */}
        <Card title="Sperrliste" description="Eine Sperre gilt für das Gerät und deckt Anrufe und Nachrichten zugleich ab; um Mitternacht endet sie von selbst." padded={false} className="min-w-0">
          {blocked.length === 0 ? (
            <p className="flex items-center gap-2 px-5 py-4 text-sm text-muted"><Sym name="shield" className="h-4 w-4" />Niemand gesperrt.</p>
          ) : (
            <ul className="divide-y divide-default text-sm">
              {blocked.map((b) => (
                <li key={b.client_hash} className="flex flex-wrap items-center gap-2 px-5 py-2.5">
                  <Sym name="ban" className="h-4 w-4 text-red-500" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-primary">{b.last_name ?? "Unbekannt"}</span>
                    <span className="block text-xs text-muted">gesperrt bis {clock(b.until)} Uhr</span>
                  </span>
                  {mayRun && (
                    <button disabled={busy} onClick={() => void act(() => phoneApi.unblock(slug, b.client_hash), "Sperre aufgehoben.")}
                      className="btn btn-ghost btn-sm">Aufheben</button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {/* ---------- Anrufe ---------- */}
      <Card title="Anrufe der letzten Sendungen" padded={false} className="mt-5"
        description="Anrufe, die älter als 30 Tage sind, räumt die Anlage von selbst weg.">
        {calls.length === 0 ? (
          <p className="px-5 py-4 text-sm text-muted">Noch kein Anruf da.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-default text-left">
                  <th className="px-5 py-2.5">Wer</th>
                  <th className="px-3 py-2.5">Wann</th>
                  <th className="px-3 py-2.5">Auf Sendung</th>
                  <th className="px-5 py-2.5">Ende</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-default">
                {calls.map((c) => (
                  <tr key={c.id}>
                    <td className="px-5 py-2.5 text-primary">
                      {c.listener_name}{c.place ? ` aus ${c.place}` : ""}
                      {c.topic && <span className="text-muted"> · „{c.topic}“</span>}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 tabular-nums text-secondary">{dayAndClock(c.created_at)}</td>
                    <td className="px-3 py-2.5 tabular-nums text-secondary">{airTime(c.air_seconds)}</td>
                    <td className="px-5 py-2.5">
                      {c.status === "done"
                        ? <span className="text-muted">{c.end_reason ? endWords[c.end_reason] ?? c.end_reason : "beendet"}</span>
                        : <Badge tone="success" dot>läuft noch</Badge>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </Page>
  );
}
